import { useMemo } from 'react';
import { deepCopy, getAverage } from '@core/helpers';
import { useByoDataContext } from './ByoContext';
import { useFilters } from '@vms/SelectFilters/SelectFiltersContext';
import { ByoChartDataItem, ByoTableChartData } from './components/ByoTable/byoTable.helpers';
import { ByoAnalyticType, ByoWord } from "@models/Byo";

type ByoKeywordItem = ByoWord['keyword'] & {
    isActive: boolean;
    color?: string;
    chartData: ByoTableChartData;
}

interface ChartColumn {
    title: string;
    tooltipSubTitle?: string;
    subColumnsCount?: number;
    subColumns?: string[];
}

interface ChartItem {
    color: string;
    isActive: boolean;
    title: string;
    data: number[][];
}

interface ChartData {
    columns: ChartColumn[];
    charts: ChartItem[];
    dashStartPointColumnIndex?: number;
    dashStartPointSubColumnIndex?: number;
}

interface BarChartItem {
    value: number;
    title: string;
    color: string;
    id: number;
}

export interface UseByoChartDataResult {
    chartData: ChartData;
    barChartData: BarChartItem[];
}

interface ByoChartItem extends ByoTableChartData {
    title: string;
    isActive: boolean;
    color: string;
    concatData: ByoChartDataItem[];
}

const CANADA_COUNTRY_CODE = 'CAN';
const COVID_YEAR = '2020';
const YEAR_PREFIX_LENGTH = 4;
const QUARTER_NAME_START_INDEX = 5;
const QUARTER_SUBCOLUMN_COUNT = 4;

const CANADA_COVID_TOOLTIP = `
    <span class="Canada-2020-interpolated-label">
        <span class="Canada-2020-interpolated-label__inner">
            extrapolated due to covid
        </span>
    </span>
`;

const sortByAverageValue = (a: ByoChartItem, b: ByoChartItem): number => {
    const averageA = getAverage(a.concatData.map(item => item.valueNumeric));
    const averageB = getAverage(b.concatData.map(item => item.valueNumeric));
    return averageB - averageA;
};

const isCanadaCovidYear = (name: string, countryCode?: string): boolean => 
    countryCode === CANADA_COUNTRY_CODE && name === COVID_YEAR;

const createColumns = (
    actualData: ByoChartDataItem[],
    actualDataQuarters: ByoChartDataItem[],
    predictedData: ByoChartDataItem[],
    predictedDataQuarters: ByoChartDataItem[],
    countryCode?: string
): ChartColumn[] => {
    const columns: ChartColumn[] = actualData.map(({ name }) => ({ 
        title: name,
        ...(isCanadaCovidYear(name, countryCode) && { tooltipSubTitle: CANADA_COVID_TOOLTIP })
    }));
    
    const quarterlyValues = [...actualDataQuarters, ...predictedDataQuarters];

    if (quarterlyValues.length) {
        const yearTitle = quarterlyValues[0].name.substring(0, YEAR_PREFIX_LENGTH);
        const subColumns = quarterlyValues.map(({ name }) => 
            name.substring(QUARTER_NAME_START_INDEX).toUpperCase()
        );

        columns.push({
            title: yearTitle,
            subColumnsCount: QUARTER_SUBCOLUMN_COUNT,
            subColumns,
        });
    }

    columns.push(...predictedData.map(({ name }) => ({ title: name })));
    
    return columns;
};

const createCharts = (byoItems: ByoChartItem[]): ChartItem[] => {
    return byoItems.map(byoItem => {
        const actualValues = byoItem.actualData.map(i => [i.valueNumeric]);
        const hasQuarters = byoItem.actualDataQuarters.length > 0 || byoItem.predictedDataQuarters.length > 0;
        const quarterlyValues = hasQuarters
            ? [[
                ...byoItem.actualDataQuarters.map(i => i.valueNumeric),
                ...byoItem.predictedDataQuarters.map(i => i.valueNumeric),
            ]]
            : [];
        const predictedValues = byoItem.predictedData.map(i => [i.valueNumeric]);

        return {
            title: byoItem.title,
            isActive: byoItem.isActive,
            color: byoItem.color,
            data: [...actualValues, ...quarterlyValues, ...predictedValues],
        };
    });
};

const mapToChartItem = (item: ByoKeywordItem): ByoChartItem => {
    const { actualData, predictedData, actualDataQuarters, predictedDataQuarters } = item.chartData;
    const concatData = [...actualData, ...actualDataQuarters, ...predictedDataQuarters, ...predictedData];
    
    return {
        title: item.name ?? '',
        isActive: item.isActive,
        color: item.color ?? '',
        actualData,
        predictedData,
        actualDataQuarters,
        predictedDataQuarters,
        concatData,
    };
};

export const useByoChartData = (keywordItems: ByoKeywordItem[]): UseByoChartDataResult => {
    const { currentCurrency: currentCountry } = useFilters();
    const { activeAnalyticsType } = useByoDataContext();

    const chartItems = useMemo(() => 
        keywordItems
            .filter(item => item.color)
            .map(item => mapToChartItem(item))
    , [keywordItems]);

    const chartData = useMemo(() => {
        if (!chartItems.length) return { columns: [], charts: [] };

        const sortedChartItems = deepCopy(chartItems).sort(sortByAverageValue);
        const { actualData, actualDataQuarters, predictedData, predictedDataQuarters } = sortedChartItems[0];

        const columns = createColumns(
            actualData, 
            actualDataQuarters, 
            predictedData, 
            predictedDataQuarters, 
            currentCountry
        );
    
        return {
            columns,
            charts: createCharts(sortedChartItems),
            dashStartPointColumnIndex: predictedData.length ? actualData.length : undefined,
            dashStartPointSubColumnIndex: predictedData.length ? actualDataQuarters.length : undefined,
        };
    }, [chartItems, currentCountry]);

    const barChartData = useMemo(() => {
        if (!chartData.charts.length || !chartData.columns.length) return [];
        
        const lastFullYearIndex = chartData.columns
            .filter((col) => !col.subColumnsCount && !col.subColumns)
            .length - 1;
        
        const decimalPlaces = activeAnalyticsType === ByoAnalyticType.Price ? 2 : 1;
        
        return chartData.charts.map(({ title, color, data }, index) => {
            const columnData = data[lastFullYearIndex] ?? data[0];
            const value = Array.isArray(columnData) ? columnData.at(-1) ?? 0 : 0;
            
            return {
                value: Number(value.toFixed(decimalPlaces)),
                title,
                color,
                id: index,
            };
        });
    }, [chartData, activeAnalyticsType]);

    return {
        chartData,
        barChartData,
    };
};
